import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { MessageSquare, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function RaiseQueryButton({ invoice, onQueryRaised, compact = false }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim()) { toast.error('Please describe your query or concern'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/invoices/${invoice.id}/raise-query`, { reason: reason.trim() });
      toast.success('Query raised successfully. The issuer will be notified.');
      setOpen(false);
      setReason('');
      if (onQueryRaised) onQueryRaised();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to raise query');
    } finally { setLoading(false); }
  };

  return (
    <>
      <Button
        variant="outline"
        size={compact ? 'sm' : 'default'}
        className="text-amber-700 border-amber-300  hover:border-amber-600 hover:bg-amber-600"
        onClick={() => setOpen(true)}
        data-testid={`raise-query-${invoice.id}`}
      >
        <MessageSquare className={`${compact ? 'h-3.5 w-3.5' : 'h-4 w-4 mr-2'}`} />
        {compact ? '' : 'Raise a Query'}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Raise a Query on Invoice #{invoice.invoice_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-[#2563eb]/10 rounded-lg p-3 text-sm">
              <p className="font-medium">₹{invoice.total_amount?.toLocaleString()}</p>
              <p className="text-slate-500 text-xs mt-0.5">Status: {invoice.status}</p>
            </div>
            <div>
              <Label className="text-sm font-medium">Describe your query <span className="text-red-500">*</span></Label>
              <textarea
                className="w-full mt-1.5 border border-slate-200 rounded-md text-sm px-3 py-2 min-h-[100px] resize-none focus:outline-none focus:ring-2 focus:ring-amber-400"
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="e.g. Incorrect amount billed, duplicate charge, wrong period..."
                data-testid="query-reason-input"
              />
            </div>
            <p className="text-xs text-slate-400">Raising a query does not block payment unless the issuer pauses collection.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={loading}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="submit-query-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Raise Query
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function QueryBadge({ invoice }) {
  if (!invoice.has_open_query) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200" data-testid={`query-badge-${invoice.id}`}>
      <MessageSquare className="h-3 w-3" /> Query Raised
    </span>
  );
}
